import React, { useMemo, memo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Button from '../Button';
import SearchSection from './SearchSection';
import ApiJobsList from './ApiJobsList';
import StaticJobsList from './StaticJobsList';
import { JobData } from '../../api/JobsService';
import { InterviewJobItem } from './JobItem';
import Spinner from '../Spinner';

interface JobListContentProps {
  isLoadingJobs: boolean;
  isLoadingJobQuestionnaires?: boolean;
  // VI-579 follow-up: true while the Select Job drawer's cold-path attached-jobs preload is
  // running. Folded into the same `isLoading` flag below so the existing big spinner gates
  // the entire job list — drawer never shows partial data while the preload is in flight.
  // Cache hits / stale-cache hydration keep this false (data is already on screen).
  isLoadingAttachedJobs?: boolean;
  jobsError: string | null;
  apiJobs: JobData[] | null;
  filteredJobs: JobData[];
  searchKeyword: string;
  selectedDivision: string;
  selectedUser: string;
  fetchJobs: () => void;
  handleSearch: (keyword: string, divisionId: string, userId: string) => void;
  handleStaticJobClick: (jobKey: 'marketingDirector' | 'devOpsEngineer') => void;
  handleJobClick: (jobId: number) => void;
  handleActionClick: (jobId?: number) => void;
  getActionButtonText: (jobId: number) => string;
  getActionButtonVariant: (jobId: number) => 'outline-primary' | 'outline-danger' | 'transparent';
  associatingJobs?: Set<number>;
  relationshipManagerEmail?: string | null;
}

const JobListContent: React.FC<JobListContentProps> = ({
  isLoadingJobs,
  isLoadingJobQuestionnaires = false,
  isLoadingAttachedJobs = false,
  jobsError,
  apiJobs,
  filteredJobs,
  searchKeyword,
  selectedDivision,
  selectedUser,
  fetchJobs,
  handleSearch,
  handleStaticJobClick,
  handleJobClick,
  handleActionClick,
  getActionButtonText,
  getActionButtonVariant,
  associatingJobs,
  relationshipManagerEmail
}) => {
  const isLoading = isLoadingJobs || isLoadingJobQuestionnaires || isLoadingAttachedJobs;

  return (
    <div>
      <SearchSection onSearch={handleSearch} />
      
      {/* Loading state */}
      {isLoading && (
        <div className="d-flex flex-column align-items-center justify-content-center p-4 gap-2">
          <Spinner size="md" color="primary" />
          <p className="mb-0">Loading jobs{isLoadingJobQuestionnaires ? ' and questionnaire data' : ''}...</p>
        </div>
      )}
      
      {/* Error state with fallback static jobs */}
      {!isLoading && jobsError && (
        <div>
          <div className="p-3 bg-danger-light text-danger border rounded mb-3 mt-3">
            <div className="d-flex align-items-center mb-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              <span className="fw-bold">Error Loading Jobs</span>
            </div>
            <p>{jobsError}</p>
            <div className="d-flex gap-2 mt-2">
              <Button 
                text="Try Again" 
                variant="outline-danger"
                onClick={fetchJobs}
              />
            </div>
          </div>
          
          {/* Show static job items as fallback when there's an error */}
          <div className="mt-4">
            <StaticJobsList
              handleStaticJobClick={handleStaticJobClick}
              handleActionClick={handleActionClick}
              getActionButtonText={getActionButtonText}
              getActionButtonVariant={getActionButtonVariant}
              message="Using demo data as fallback."
              associatingJobs={associatingJobs}
            />
          </div>
        </div>
      )}
      
      {/* No jobs state with fallback static jobs */}
      {!isLoading && !jobsError && apiJobs?.length === 0 && (
        <StaticJobsList
          handleStaticJobClick={handleStaticJobClick}
          handleActionClick={handleActionClick}
          getActionButtonText={getActionButtonText}
          getActionButtonVariant={getActionButtonVariant}
          message="No jobs found. Using demo data instead."
          associatingJobs={associatingJobs}
        />
      )}
      
      {/* Jobs list */}
      {!isLoading && !jobsError && filteredJobs.length > 0 && (
        <ApiJobsList
          jobs={filteredJobs}
          handleJobClick={handleJobClick}
          handleActionClick={handleActionClick}
          getActionButtonText={getActionButtonText}
          getActionButtonVariant={getActionButtonVariant}
          associatingJobs={associatingJobs}
          relationshipManagerEmail={relationshipManagerEmail}
        />
      )}

      {/* No jobs matching filters */}
      {!isLoading && !jobsError && filteredJobs.length === 0 && (apiJobs && apiJobs.length > 0) && (
        <div className="text-center p-4">
          <p>
            {(searchKeyword || selectedDivision !== 'all' || selectedUser !== 'all') ? 
              `No jobs found matching the selected filters.` :
              'No jobs found.'
            }
          </p>
          <Button 
            text="Clear Filters" 
            variant="outline-primary"
            onClick={() => {
              handleSearch('', 'all', 'all');
            }}
          />
        </div>
      )}
    </div>
  );
};

export const InterviewJobListContent: React.FC<{
  jobData: JobData[] | null;
  isLoading?: boolean;
  isLoadingAllJobs?: boolean;
  handleJobClick: (jobId: number) => void;
  selectedJob?: JobData | null;
  jobsWithQuestionnaires?: Set<number>;
  isLoadingJobQuestionnaires?: boolean;
}> = memo(({ jobData, isLoading = false, isLoadingAllJobs = false, handleJobClick, selectedJob, jobsWithQuestionnaires, isLoadingJobQuestionnaires }) => {

  // fix bug tablet AS-5343
  const isTablet = window.innerWidth >= 832 && window.innerWidth <= 991;
  
  // Memoize the job list to prevent re-renders
  const memoizedJobs = useMemo(() => {
    if (!jobData) return [];
    return jobData;
  }, [jobData]);

  const isPageLoading = isLoading || isLoadingAllJobs;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      padding: '1rem',
      marginTop: isTablet ? '15rem' : '0',
    }}>
      {/* Spinner replaces the entire list while loading */}
      {isPageLoading && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          gap: '0.75rem',
          color: '#6c757d',
        }}>
          <Spinner size="xl"/>
        </div>
      )}

      {/* Jobs list - only render when not loading */}
      {!isPageLoading && memoizedJobs.length > 0 && memoizedJobs.map((job) => (
        <InterviewJobItem 
          key={job.id}
          title={job.title} 
          location={job.locations[0].city + ', ' + job.locations[0].region} 
          reqId={job.requisitionName} 
          jobId={job.id.toString()} 
          created={new Date(job.created).toLocaleDateString('en-US', {
            month: 'numeric',
            day: '2-digit', 
            year: 'numeric'
          })} 
          status={job.status}
          onClick={() => handleJobClick(job.id)}
          isSelected={selectedJob?.id === job.id}
        />
      ))}

      {/* Empty state only when not loading and no jobs */}
      {!isPageLoading && memoizedJobs.length === 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          color: '#6c757d'
        }}>
          <p>No jobs found.</p>
        </div>
      )}
    </div>
  );
});

InterviewJobListContent.displayName = 'InterviewJobListContent';

export default JobListContent; 